import React, { useState, useEffect, useCallback } from 'react';
import { clinicService } from '../../../services/api';
import {
  buildServiceToDoctorsMap,
  formatQAAnswer,
  formatDoctorDisplay,
  DEFAULT_FORMAT_WITH_DOCTORS,
  DEFAULT_FORMAT_NO_DOCTORS,
  DEFAULT_DOCTOR_LANGUAGES,
} from './parseServiceProvided';

const DEFAULT_TAG = 'Appointment Flow';
const LANGUAGE = 'zh_HK';

const LANG_OPTIONS = [
  { key: 'primary_name', label: 'Primary name' },
  { key: 'zh_HK', label: 'zh_HK (Traditional Chinese)' },
  { key: 'en_US', label: 'en_US (English)' },
  { key: 'zh_CN', label: 'zh_CN (Simplified Chinese)' },
];

const Step2_Generate = ({ clinicId, onBack, onNext, error, setError }) => {
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [serviceMap, setServiceMap] = useState(null);
  const [qaItems, setQAItems] = useState([]);
  const [formatWithDoctors, setFormatWithDoctors] = useState(DEFAULT_FORMAT_WITH_DOCTORS);
  const [formatNoDoctors, setFormatNoDoctors] = useState(DEFAULT_FORMAT_NO_DOCTORS);
  const [doctorLanguages, setDoctorLanguages] = useState(DEFAULT_DOCTOR_LANGUAGES);

  useEffect(() => {
    const load = async () => {
      if (!clinicId) return;
      setLoading(true);
      setError('');
      try {
        const data = await clinicService.getClinicDoctors(clinicId);
        const doctorList = data.doctors || [];
        setDoctors(doctorList);
        setServiceMap(buildServiceToDoctorsMap(doctorList));
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Failed to load doctors');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clinicId, setError]);

  const regenerateItems = useCallback(() => {
    if (!serviceMap) return;
    const items = [];
    for (const [serviceName, doctorListForService] of serviceMap.entries()) {
      items.push({
        question: serviceName,
        answer: formatQAAnswer(
          serviceName,
          doctorListForService,
          formatWithDoctors,
          formatNoDoctors,
          doctorLanguages
        ),
        tags: [DEFAULT_TAG],
        language: LANGUAGE,
        doctors: doctorListForService,
      });
    }
    items.sort((a, b) => a.question.localeCompare(b.question));
    setQAItems(items);
  }, [serviceMap, formatWithDoctors, formatNoDoctors, doctorLanguages]);

  useEffect(() => {
    if (serviceMap) regenerateItems();
  }, [serviceMap, regenerateItems]);

  const handleNext = () => {
    if (qaItems.length === 0) {
      setError('No services found from doctors. Check that doctors have service_provided.');
      return;
    }
    setError('');
    onNext({ qaItems });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2" />
        <p className="text-gray-600">Loading doctors and building service mapping...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 2: Generate QA</h2>
        <p className="text-gray-600">
          Found {qaItems.length} services from {doctors.length} doctors.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Format when doctors exist (placeholders: {'{service_name}'}, {'{doctor_list}'})
          </label>
          <textarea
            value={formatWithDoctors}
            onChange={(e) => setFormatWithDoctors(e.target.value)}
            className="w-full p-2 border rounded text-sm font-mono min-h-[100px]"
            placeholder={DEFAULT_FORMAT_WITH_DOCTORS}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Doctor name languages (order: primary_name, zh_HK, en_US, zh_CN). Duplicates shown once.
          </label>
          <div className="flex flex-wrap gap-4">
            {LANG_OPTIONS.map((opt) => (
              <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={doctorLanguages[opt.key] ?? false}
                  onChange={(e) =>
                    setDoctorLanguages((prev) => ({
                      ...prev,
                      [opt.key]: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Format when no doctors (placeholder: {'{service_name}'})
          </label>
          <textarea
            value={formatNoDoctors}
            onChange={(e) => setFormatNoDoctors(e.target.value)}
            className="w-full p-2 border rounded text-sm font-mono min-h-[80px]"
            placeholder={DEFAULT_FORMAT_NO_DOCTORS}
          />
        </div>
      </div>

      <div className="border rounded-lg max-h-64 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              <th className="text-left p-2">Service (Question)</th>
              <th className="text-left p-2">Doctors</th>
            </tr>
          </thead>
          <tbody>
            {qaItems.map((item, i) => (
              <tr key={i} className="border-t">
                <td className="p-2">{item.question}</td>
                <td className="p-2">
                  {item.doctors?.length > 0
                    ? item.doctors.map((d) => formatDoctorDisplay(d, doctorLanguages)).join(', ')
                    : '(none)'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={qaItems.length === 0}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          Next ({qaItems.length} QA items)
        </button>
      </div>
    </div>
  );
};

export default Step2_Generate;
